import React from 'react'
import { Headphones, Mail } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import GeorgiaMark from '@/components/Auth/GeorgiaMark'
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@/components/Consent/constants'
import { cn } from '@/lib/utils'
import type { SignInBrand } from './useSignInBrand'

/**
 * The sign-in page's shell, from the mocks on the ClickUp task: a white sign-in panel
 * dominated by the customer's brand (Georgia only in the "Powered by" line) and a dark
 * green information panel. Presentational: `Auth` fills the form slot, and the admin
 * Settings tab renders the same component as its preview. Below 900px the panels stack
 * with the information panel first.
 */

const MAX_MODULES_SHOWN = 5

export interface SignInPageProps {
  brand: SignInBrand
  /** The link's modules, in its order. Empty hides the block (there is no link). */
  modules: string[]
  /** The sign-in form, the redemption notices and the errors. */
  children?: React.ReactNode
  /** The cookie preferences control; omitted where the consent store is not around (preview, tests). */
  cookiesLink?: React.ReactNode
  className?: string
}

/** First letters of the first two words, for an organisation without a logo. */
export const initialsOf = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('')

/** The customer mark: the logo as is on white, else initials or the Georgia mark in brand green. */
const Mark: React.FC<{ brand: SignInBrand }> = ({ brand }) => (
  <div className="flex h-[150px] w-[150px] shrink-0 items-center justify-center overflow-hidden text-[#0F4C3A]" data-testid="brand-tile-mark">
    {brand.logoUrl ? (
      <img src={brand.logoUrl} alt="" className="h-full w-full object-contain" />
    ) : brand.isGeorgia ? (
      <GeorgiaMark className="h-24 w-24" />
    ) : (
      <span className="text-6xl font-extrabold tracking-tight">{initialsOf(brand.name)}</span>
    )}
  </div>
)

const SignInPage: React.FC<SignInPageProps> = ({ brand, modules, children, cookiesLink, className }) => {
  const { t } = useTranslation()
  const shown = modules.slice(0, MAX_MODULES_SHOWN)
  const rest = modules.length - shown.length

  return (
    <div className={cn('grid min-h-dvh w-full grid-cols-1 bg-white font-[Inter] text-[#0F4C3A] antialiased min-[901px]:grid-cols-2', className)}>
      {/* Sign-in panel. Second on small screens, first from 901px. */}
      <section className="relative order-2 flex min-h-dvh flex-col bg-white px-6 py-8 min-[901px]:order-1 min-[901px]:px-12 min-[901px]:py-8">
        <div className="flex flex-1 flex-col items-center justify-center py-10">
          <div className="flex w-full max-w-[400px] flex-col items-center">
            <div className="mb-5">
              <Mark brand={brand} />
            </div>
            <h1 className="mb-1.5 line-clamp-2 text-center text-2xl font-extrabold tracking-[-0.5px] text-[#0F4C3A]">{brand.name}</h1>
            <p className="mb-8 text-center text-[13.5px] text-[#6B7D72]">{brand.subtitle}</p>

            <div className="flex w-full flex-col">{children}</div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-1.5 text-[11.5px] text-[#6B7D72]">
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="px-1.5 hover:text-[#0F4C3A] hover:underline">
                {t('auth.page.privacy')}
              </a>
              <span aria-hidden="true">·</span>
              <a href={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer" className="px-1.5 hover:text-[#0F4C3A] hover:underline">
                {t('auth.page.terms')}
              </a>
              {cookiesLink && (
                <>
                  <span aria-hidden="true">·</span>
                  {cookiesLink}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-1.5 text-[11px] tracking-[0.4px] text-[#6B7D72]">
          <div className="text-[10px] font-semibold uppercase tracking-[1.5px]">{t('auth.page.poweredBy')}</div>
          <div className="inline-flex items-center gap-1.5">
            <div className="inline-flex h-5 w-5 items-center justify-center rounded-[5px] bg-gradient-to-br from-[#0F4C3A] to-[#073529] text-[#C5F26A]">
              <GeorgiaMark className="h-3 w-3" />
            </div>
            <div className="text-[13px] font-bold text-[#0F4C3A]">Georgia</div>
          </div>
        </div>
      </section>

      {/* Information panel. First on small screens, second from 901px. */}
      <aside className="relative order-1 flex flex-col justify-center overflow-hidden bg-gradient-to-br from-[#0F4C3A] to-[#073529] px-6 py-12 text-white min-[901px]:order-2 min-[901px]:px-14 min-[901px]:pt-14 min-[901px]:pb-12">
        <div className="pointer-events-none absolute -top-[120px] -right-[120px] h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(197,242,106,0.14)_0%,transparent_70%)]" />
        <div className="pointer-events-none absolute -bottom-[140px] -left-[140px] h-[380px] w-[380px] rounded-full bg-[radial-gradient(circle,rgba(197,242,106,0.08)_0%,transparent_70%)]" />

        <div className="relative z-10 w-full max-w-[480px]">
          <h2 className="mb-4 text-[28px] leading-[1.1] font-extrabold tracking-[-1px] text-white min-[901px]:text-4xl">
            <Trans i18nKey="auth.page.headline" values={{ customer: brand.name }} components={{ highlight: <span className="text-[#C5F26A]" /> }} />
          </h2>
          <p className="mb-9 text-[15px] leading-[1.6] text-white/75">{brand.lede}</p>

          {shown.length > 0 && (
            <div className="mb-4 rounded-[14px] border border-white/8 bg-white/4 px-[22px] py-5" data-testid="modules-block">
              <div className="mb-3.5 flex items-center gap-2 text-[11px] font-bold tracking-[1.3px] text-[#C5F26A] uppercase">
                <Headphones className="h-[15px] w-[15px]" aria-hidden="true" />
                {t('auth.page.modulesIncluded')}
              </div>
              <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                {shown.map((name, index) => (
                  <li key={`${name}-${index}`} className="flex items-center gap-2.5 text-sm font-medium text-white/90">
                    <span aria-hidden="true" className="h-[5px] w-[5px] shrink-0 rounded-full bg-[#A4D447]" />
                    <span>{name}</span>
                    {index === shown.length - 1 && rest > 0 && (
                      <span className="ml-0.5 inline-flex items-center rounded-full bg-[#C5F26A]/14 px-2 py-0.5 text-[10.5px] font-bold tracking-[0.4px] text-[#C5F26A]">
                        {t('auth.page.more', { count: rest })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-3.5 rounded-[14px] border border-[#C5F26A]/20 bg-[#C5F26A]/8 px-5 py-4">
            <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#C5F26A]/20 text-[#C5F26A]">
              <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-white">{t('auth.page.contactLabel')}</div>
              <a href={`mailto:${brand.contactEmail}`} className="block truncate text-sm font-medium text-[#C5F26A] hover:underline">
                {brand.contactEmail}
              </a>
            </div>
          </div>
        </div>
      </aside>
    </div>
  )
}

export default SignInPage
